import React, { useState, useEffect } from 'react';
import { api, useAuth } from '../App';
import { toast } from 'sonner';
import Sidebar from '../components/layout/Sidebar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Users,
  Plus,
  Search,
  MoreHorizontal,
  Link,
  Copy,
  DollarSign,
  TrendingUp,
  UserCheck,
  Building,
  Mail,
  Phone,
  ExternalLink,
  CheckCircle,
  Clock,
  XCircle,
  Edit,
  Trash2,
  Key
} from 'lucide-react';

const ReferralBrokersPage = () => {
  const { user } = useAuth();
  const [brokers, setBrokers] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showStatsDialog, setShowStatsDialog] = useState(false);
  const [selectedBroker, setSelectedBroker] = useState(null);
  const [brokerStats, setBrokerStats] = useState(null);
  
  // Form state
  const [brokerForm, setBrokerForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    commission_type: 'percentage',
    commission_value: 10,
    is_internal: false,
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, [activeFilter]);

  const loadData = async () => {
    try {
      const params = new URLSearchParams();
      if (activeFilter === 'active') params.append('is_active', 'true');
      if (activeFilter === 'inactive') params.append('is_active', 'false');
      if (activeFilter === 'internal') params.append('is_internal', 'true');
      if (activeFilter === 'external') params.append('is_internal', 'false');
      
      const [brokersRes, commissionsRes] = await Promise.all([
        api.get(`/referral-brokers?${params.toString()}`),
        api.get('/referral-commissions?limit=20')
      ]);
      
      setBrokers(brokersRes.data);
      setCommissions(commissionsRes.data);
    } catch (error) {
      toast.error('Failed to load referral brokers');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBroker = async () => {
    try {
      await api.post('/referral-brokers', brokerForm);
      toast.success('Referral broker created');
      setShowAddDialog(false);
      setBrokerForm({
        name: '',
        email: '',
        phone: '',
        company: '',
        commission_type: 'percentage',
        commission_value: 10,
        is_internal: false,
        notes: ''
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create referral broker');
    }
  };

  const handleViewStats = async (broker) => {
    setSelectedBroker(broker);
    try {
      const res = await api.get(`/referral-brokers/${broker.broker_id}/stats`);
      setBrokerStats(res.data);
      setShowStatsDialog(true);
    } catch (error) {
      toast.error('Failed to load broker stats');
    }
  };

  const handleToggleActive = async (broker) => {
    try {
      await api.put(`/referral-brokers/${broker.broker_id}`, {
        is_active: !broker.is_active
      });
      toast.success(broker.is_active ? 'Broker deactivated' : 'Broker activated');
      loadData();
    } catch (error) {
      toast.error('Failed to update broker');
    }
  };

  const handleCreatePortalAccess = async (broker) => {
    try {
      await api.post(`/referral-brokers/${broker.broker_id}/create-portal-access`);
      toast.success('Portal access created. Check logs for temporary password.');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create portal access');
    }
  };

  const handleUpdateCommissionStatus = async (commissionId, newStatus) => {
    try {
      await api.put(`/referral-commissions/${commissionId}`, { status: newStatus });
      toast.success(`Commission ${newStatus}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update commission');
    }
  };

  const copyReferralLink = (broker) => {
    const link = `${window.location.origin}/refer/${broker.referral_code}`;
    navigator.clipboard.writeText(link);
    toast.success('Referral link copied to clipboard');
  };

  const getCommissionStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary"><Clock className="mr-1 h-3 w-3" />Pending</Badge>;
      case 'approved':
        return <Badge className="bg-blue-500"><CheckCircle className="mr-1 h-3 w-3" />Approved</Badge>;
      case 'paid':
        return <Badge className="bg-green-500"><CheckCircle className="mr-1 h-3 w-3" />Paid</Badge>;
      case 'cancelled':
        return <Badge variant="destructive"><XCircle className="mr-1 h-3 w-3" />Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const filteredBrokers = brokers.filter(broker => 
    broker.name.toLowerCase().includes(search.toLowerCase()) ||
    broker.email.toLowerCase().includes(search.toLowerCase()) ||
    broker.referral_code.toLowerCase().includes(search.toLowerCase())
  );

  const totalStats = {
    totalBrokers: brokers.length,
    activeBrokers: brokers.filter(b => b.is_active).length,
    totalLeads: brokers.reduce((sum, b) => sum + (b.total_leads || 0), 0),
    totalCommissions: brokers.reduce((sum, b) => sum + (b.total_commission_earned || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <main className="flex-1 ml-64 p-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-muted rounded w-48"></div>
            <div className="grid grid-cols-4 gap-4">
              {[1,2,3,4].map(i => <div key={i} className="h-24 bg-muted rounded-xl"></div>)}
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background" data-testid="referral-brokers-page">
      <Sidebar />
      <main className="flex-1 ml-64 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
              <Users className="h-8 w-8" />
              Referral Brokers
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage referral partners and track commissions
            </p>
          </div>
          {(user?.role === 'admin' || user?.role === 'manager') && (
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button data-testid="add-broker-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Referral Broker
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Add Referral Broker</DialogTitle>
                  <DialogDescription>
                    Create a new referral partner with unique tracking link
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Name *</Label>
                      <Input
                        value={brokerForm.name}
                        onChange={(e) => setBrokerForm({...brokerForm, name: e.target.value})}
                        placeholder="John Smith"
                        data-testid="broker-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={brokerForm.email}
                        onChange={(e) => setBrokerForm({...brokerForm, email: e.target.value})}
                        placeholder="john@example.com"
                        data-testid="broker-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input
                        value={brokerForm.phone}
                        onChange={(e) => setBrokerForm({...brokerForm, phone: e.target.value})}
                        placeholder="555-123-4567"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Company</Label>
                      <Input
                        value={brokerForm.company}
                        onChange={(e) => setBrokerForm({...brokerForm, company: e.target.value})}
                        placeholder="ABC Realty"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Commission Type</Label>
                      <Select
                        value={brokerForm.commission_type}
                        onValueChange={(v) => setBrokerForm({...brokerForm, commission_type: v})}
                      >
                        <SelectTrigger data-testid="commission-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percentage">Percentage</SelectItem>
                          <SelectItem value="fixed">Fixed Amount</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>
                        {brokerForm.commission_type === 'percentage' ? 'Percentage (%)' : 'Fixed Amount ($)'}
                      </Label>
                      <Input
                        type="number"
                        value={brokerForm.commission_value}
                        onChange={(e) => setBrokerForm({...brokerForm, commission_value: parseFloat(e.target.value)})}
                        placeholder={brokerForm.commission_type === 'percentage' ? '10' : '500'}
                        data-testid="commission-value"
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={brokerForm.is_internal}
                      onCheckedChange={(checked) => setBrokerForm({...brokerForm, is_internal: checked})}
                      id="is_internal"
                    />
                    <Label htmlFor="is_internal">Internal Team Member (vs External Partner)</Label>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea
                      value={brokerForm.notes}
                      onChange={(e) => setBrokerForm({...brokerForm, notes: e.target.value})}
                      placeholder="Any additional notes..."
                      rows={2}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                  <Button 
                    onClick={handleCreateBroker} 
                    disabled={!brokerForm.name || !brokerForm.email}
                    data-testid="save-broker-btn"
                  >
                    Create Broker
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="hover-lift">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Brokers</p>
                  <p className="text-3xl font-bold">{totalStats.totalBrokers}</p>
                </div>
                <div className="p-3 bg-primary/10 rounded-xl">
                  <Users className="h-6 w-6 text-primary" />
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                {totalStats.activeBrokers} active
              </p>
            </CardContent>
          </Card>
          
          <Card className="hover-lift">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Leads</p>
                  <p className="text-3xl font-bold">{totalStats.totalLeads}</p>
                </div>
                <div className="p-3 bg-blue-500/10 rounded-xl">
                  <UserCheck className="h-6 w-6 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="hover-lift">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Commissions</p>
                  <p className="text-3xl font-bold">${totalStats.totalCommissions.toLocaleString()}</p>
                </div>
                <div className="p-3 bg-green-500/10 rounded-xl">
                  <DollarSign className="h-6 w-6 text-green-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="hover-lift">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pending Commissions</p>
                  <p className="text-3xl font-bold">
                    {commissions.filter(c => c.status === 'pending' || c.status === 'approved').length}
                  </p>
                </div>
                <div className="p-3 bg-amber-500/10 rounded-xl">
                  <Clock className="h-6 w-6 text-amber-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="brokers" className="space-y-6">
          <TabsList>
            <TabsTrigger value="brokers">Referral Brokers</TabsTrigger>
            <TabsTrigger value="commissions">Commissions</TabsTrigger>
          </TabsList>

          {/* Brokers Tab */}
          <TabsContent value="brokers">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Referral Brokers</CardTitle>
                    <CardDescription>Manage your referral partners and track their performance</CardDescription>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search brokers..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10 w-[250px]"
                        data-testid="search-brokers"
                      />
                    </div>
                    <Select value={activeFilter} onValueChange={setActiveFilter}>
                      <SelectTrigger className="w-[150px]">
                        <SelectValue placeholder="Filter" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Brokers</SelectItem>
                        <SelectItem value="active">Active Only</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                        <SelectItem value="internal">Internal</SelectItem>
                        <SelectItem value="external">External</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Broker</TableHead>
                      <TableHead>Referral Code</TableHead>
                      <TableHead>Commission</TableHead>
                      <TableHead>Leads</TableHead>
                      <TableHead>Earned</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredBrokers.length > 0 ? (
                      filteredBrokers.map((broker) => (
                        <TableRow key={broker.broker_id} data-testid={`broker-row-${broker.broker_id}`}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{broker.name}</p>
                              <p className="text-sm text-muted-foreground">{broker.email}</p>
                              {broker.company && (
                                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                                  <Building className="h-3 w-3" />
                                  {broker.company}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <code className="bg-muted px-2 py-1 rounded text-sm font-mono">
                                {broker.referral_code}
                              </code>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => copyReferralLink(broker)}
                                data-testid={`copy-link-${broker.broker_id}`}
                              >
                                <Copy className="h-3 w-3" />
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">
                              {broker.commission_type === 'percentage' 
                                ? `${broker.commission_value}%` 
                                : `$${broker.commission_value}`
                              }
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="font-medium">{broker.total_leads || 0}</span>
                          </TableCell>
                          <TableCell>
                            <span className="font-medium text-green-600">
                              ${(broker.total_commission_earned || 0).toLocaleString()}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Badge variant={broker.is_active ? 'default' : 'secondary'}>
                                {broker.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                              <Badge variant="outline">
                                {broker.is_internal ? 'Internal' : 'External'}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" data-testid={`broker-menu-${broker.broker_id}`}>
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleViewStats(broker)}>
                                  <TrendingUp className="mr-2 h-4 w-4" />
                                  View Stats
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => copyReferralLink(broker)}>
                                  <Link className="mr-2 h-4 w-4" />
                                  Copy Referral Link
                                </DropdownMenuItem>
                                {!broker.user_id && (
                                  <DropdownMenuItem onClick={() => handleCreatePortalAccess(broker)}>
                                    <Key className="mr-2 h-4 w-4" />
                                    Create Portal Access
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={() => handleToggleActive(broker)}>
                                  {broker.is_active ? 'Deactivate' : 'Activate'}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          No referral brokers found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Commissions Tab */}
          <TabsContent value="commissions">
            <Card>
              <CardHeader>
                <CardTitle>Referral Commissions</CardTitle>
                <CardDescription>Track and manage commission payouts</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Broker</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Deal</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {commissions.length > 0 ? (
                      commissions.map((commission) => (
                        <TableRow key={commission.commission_id} data-testid={`commission-row-${commission.commission_id}`}>
                          <TableCell>
                            <p className="font-medium">{commission.broker?.name || 'N/A'}</p>
                            <p className="text-xs text-muted-foreground">{commission.broker?.email}</p>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm">
                              {commission.contact?.first_name} {commission.contact?.last_name}
                            </p>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="text-sm">{commission.deal?.loan_type}</p>
                              <p className="text-xs text-muted-foreground">
                                ${(commission.deal?.amount || 0).toLocaleString()}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-semibold text-green-600">
                                ${commission.amount?.toLocaleString()}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {commission.calculation_note}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>
                            {getCommissionStatusBadge(commission.status)}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {new Date(commission.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            {(user?.role === 'admin' || user?.role === 'manager') && commission.status !== 'paid' && commission.status !== 'cancelled' && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {commission.status === 'pending' && (
                                    <DropdownMenuItem onClick={() => handleUpdateCommissionStatus(commission.commission_id, 'approved')}>
                                      Approve
                                    </DropdownMenuItem>
                                  )}
                                  {(commission.status === 'pending' || commission.status === 'approved') && (
                                    <DropdownMenuItem onClick={() => handleUpdateCommissionStatus(commission.commission_id, 'paid')}>
                                      Mark as Paid
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem 
                                    className="text-destructive"
                                    onClick={() => handleUpdateCommissionStatus(commission.commission_id, 'cancelled')}
                                  >
                                    Cancel
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          No commissions yet
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Stats Dialog */}
        <Dialog open={showStatsDialog} onOpenChange={setShowStatsDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Broker Statistics: {selectedBroker?.name}</DialogTitle>
            </DialogHeader>
            {brokerStats && (
              <div className="space-y-6 py-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-muted rounded-lg text-center">
                    <p className="text-2xl font-bold">{brokerStats.total_leads}</p>
                    <p className="text-sm text-muted-foreground">Total Leads</p>
                  </div>
                  <div className="p-4 bg-muted rounded-lg text-center">
                    <p className="text-2xl font-bold">{brokerStats.total_deals_closed}</p>
                    <p className="text-sm text-muted-foreground">Deals Closed</p>
                  </div>
                  <div className="p-4 bg-muted rounded-lg text-center">
                    <p className="text-2xl font-bold">{brokerStats.conversion_rate}%</p>
                    <p className="text-sm text-muted-foreground">Conversion Rate</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-green-500/10 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-600">
                      ${brokerStats.total_commission_earned?.toLocaleString()}
                    </p>
                    <p className="text-sm text-muted-foreground">Total Earned</p>
                  </div>
                  <div className="p-4 bg-blue-500/10 rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-600">
                      ${brokerStats.total_commission_paid?.toLocaleString()}
                    </p>
                    <p className="text-sm text-muted-foreground">Paid Out</p>
                  </div>
                  <div className="p-4 bg-amber-500/10 rounded-lg text-center">
                    <p className="text-2xl font-bold text-amber-600">
                      ${brokerStats.total_commission_pending?.toLocaleString()}
                    </p>
                    <p className="text-sm text-muted-foreground">Pending</p>
                  </div>
                </div>
                
                {brokerStats.leads?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-3">Recent Leads</h4>
                    <div className="space-y-2">
                      {brokerStats.leads.map((lead) => (
                        <div key={lead.contact_id} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                          <span>{lead.first_name} {lead.last_name}</span>
                          <Badge variant="outline">{lead.loan_types?.join(', ') || 'N/A'}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default ReferralBrokersPage;
